/**
 * TrackerAlertsPanel Component
 * 
 * Shows GPS tracker alerts for the platform owner dashboard.
 * Displays: offline trackers, vehicles in recovery mode, overdue vehicles with trackers.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Satellite, 
  AlertTriangle, 
  MapPin, 
  Clock, 
  RefreshCw,
  ChevronRight,
  Shield,
  Radio,
  Car
} from 'lucide-react';

const TrackerAlertsPanel = ({ compact = false }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/platform-owner/tracker/alerts');
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch tracker alerts:', err);
      setError('Failed to load tracker alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    // Refresh every 5 minutes
    const interval = setInterval(fetchAlerts, 300000);
    return () => clearInterval(interval);
  }, []);

  const getAlertIcon = (type) => {
    switch (type) {
      case 'recovery_mode_active': return <Shield className="w-4 h-4 text-red-600" />;
      case 'tracker_offline': return <Radio className="w-4 h-4 text-amber-600" />;
      case 'booking_overdue_tracked': return <Car className="w-4 h-4 text-orange-600" />;
      default: return <AlertTriangle className="w-4 h-4 text-slate-600" />;
    }
  };

  const getAlertBg = (severity, type) => {
    if (type === 'recovery_mode_active') return 'bg-red-50 border-red-300 hover:bg-red-100';
    if (severity === 'critical') return 'bg-orange-50 border-orange-300 hover:bg-orange-100';
    return 'bg-amber-50 border-amber-200 hover:bg-amber-100';
  };

  if (loading && !data) {
    return (
      <Card className="border-slate-200" data-testid="tracker-alerts-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Satellite className="w-5 h-5 text-blue-600" />
            Tracker Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-6">
            <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50" data-testid="tracker-alerts-panel">
        <CardContent className="py-6">
          <p className="text-red-600 text-sm">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchAlerts} className="mt-2">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { alerts, total_alerts, critical_count, warning_count } = data || {};
  const hasCritical = critical_count > 0;
  const hasAlerts = total_alerts > 0;

  // Compact view
  if (compact) {
    return (
      <Card 
        className={`border ${hasCritical ? 'border-red-300 bg-red-50/50' : hasAlerts ? 'border-amber-200' : 'border-slate-200'}`}
        data-testid="tracker-alerts-panel-compact"
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Satellite className={`w-5 h-5 ${hasCritical ? 'text-red-600' : 'text-blue-600'}`} />
              Tracker Alerts
            </CardTitle>
            {total_alerts > 0 && (
              <Badge className={hasCritical ? 'bg-red-600' : 'bg-amber-500'}>
                {total_alerts}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {!hasAlerts ? (
            <div className="flex items-center gap-2 text-green-600 py-2">
              <Satellite className="w-4 h-4" />
              <span className="text-sm">All trackers operational</span>
            </div>
          ) : (
            <div className="space-y-1">
              {critical_count > 0 && (
                <div className="flex items-center gap-2 text-red-600">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm font-medium">{critical_count} critical alert(s)</span>
                </div>
              )}
              {warning_count > 0 && (
                <div className="flex items-center gap-2 text-amber-600">
                  <Radio className="w-4 h-4" />
                  <span className="text-sm">{warning_count} tracker warning(s)</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full view
  return (
    <Card className="border-slate-200" data-testid="tracker-alerts-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Satellite className={`w-5 h-5 ${hasCritical ? 'text-red-600' : 'text-blue-600'}`} />
            GPS Tracker Alerts
            {hasCritical && (
              <Badge className="bg-red-600 animate-pulse">
                {critical_count} CRITICAL
              </Badge>
            )}
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchAlerts}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Security alerts for vehicles with GPS trackers
        </p>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Summary badges */}
        <div className="flex flex-wrap gap-2">
          <Badge className={hasCritical ? 'bg-red-600' : 'bg-slate-400'}>
            {critical_count} Critical
          </Badge>
          <Badge className={warning_count > 0 ? 'bg-amber-500' : 'bg-slate-400'}>
            {warning_count} Warnings
          </Badge>
        </div>

        {!hasAlerts ? (
          <div className="flex items-center justify-center gap-2 text-green-600 py-6 bg-green-50 rounded-lg">
            <Satellite className="w-5 h-5" />
            <span className="font-medium">All trackers operational - No alerts</span>
          </div>
        ) : (
          <div className="space-y-2">
            {alerts?.slice(0, 10).map((alert, idx) => (
              <div
                key={`alert-${idx}`}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${getAlertBg(alert.severity, alert.type)}`}
                onClick={() => alert.action_url && navigate(alert.action_url)}
                data-testid={`tracker-alert-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-slate-800">
                        {alert.vehicle_name}
                      </span>
                      <Badge className={
                        alert.type === 'recovery_mode_active' 
                          ? 'bg-red-600 text-white text-xs' 
                          : alert.severity === 'critical'
                            ? 'bg-orange-100 text-orange-800 text-xs'
                            : 'bg-amber-100 text-amber-800 text-xs'
                      }>
                        {alert.type.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{alert.message}</p>
                    
                    {/* Show location if available */}
                    {alert.last_location && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                        <MapPin className="w-3 h-3" />
                        <span>
                          {alert.last_location.address || 
                           `${alert.last_location.lat?.toFixed(4)}, ${alert.last_location.lng?.toFixed(4)}`}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    size="sm" 
                    variant="outline"
                    className={
                      alert.type === 'recovery_mode_active'
                        ? 'border-red-300 text-red-700 hover:bg-red-100'
                        : 'border-amber-300 text-amber-700 hover:bg-amber-100'
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (alert.action_url) navigate(alert.action_url);
                    }}
                  >
                    {alert.action_label || 'View'}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            ))}
            
            {alerts?.length > 10 && (
              <p className="text-xs text-slate-500 text-center">
                +{alerts.length - 10} more alerts
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TrackerAlertsPanel;
